import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@mantine/form';
import {
  Container,
  Title,
  Text,
  Select,
  TextInput,
  Button,
  Group,
  Stack,
  Card,
  Divider,
  Loader,
  Alert,
  ActionIcon,
} from '@mantine/core';
import { IconArrowLeft, IconPlus, IconInfoCircle, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { api } from '@/services/api';
import { ServiceHealthWarning } from '@/components/ServiceHealthWarning';
import { parseUTCDate } from '@/utils/date';

interface CreateDatabaseFormValues {
  name: string;
  snapshot: string;
  ttl_hours: number;
}

export function CreateDatabasePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<CreateDatabaseFormValues>({
    initialValues: {
      name: '',
      snapshot: '',
      ttl_hours: 168, // 7 days default
    },
    validate: {
      name: (value: string) => {
        if (!value) return 'Database name is required';
        // Kubernetes DNS-1035 validation
        const nameRegex = /^[a-z]([a-z0-9-]{0,61}[a-z0-9])?$/;
        if (!nameRegex.test(value)) {
          return 'Name must start with a letter, contain only lowercase letters, numbers, and hyphens, and be 63 characters or less';
        }
        return null;
      },
      snapshot: (value: string) => (!value ? 'Snapshot is required' : null),
    },
  });

  // Fetch available snapshots
  const {
    data: snapshotsData,
    isLoading: snapshotsLoading,
    error: snapshotsError,
  } = useQuery({
    queryKey: ['database-snapshots'],
    queryFn: () => api.listDatabaseSnapshots(),
  });

  // Set default snapshot when data loads
  useEffect(() => {
    if (snapshotsData?.items && snapshotsData.items.length > 0 && !form.values.snapshot) {
      // Use the first snapshot (should be the latest)
      form.setFieldValue('snapshot', snapshotsData.items[0].name);
    }
  }, [snapshotsData]);

  // Create database mutation
  const createMutation = useMutation({
    mutationFn: (values: CreateDatabaseFormValues) => api.createDatabase(values),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['databases'] });
      notifications.show({
        title: 'Database Created',
        message: 'Your database is being created. Redirecting to details page...',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      // Redirect to database detail page after short delay
      setTimeout(() => navigate(`/databases/${data.id}`), 1000);
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Failed to Create Database',
        message: error.message || 'An unexpected error occurred',
        color: 'red',
      });
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    createMutation.mutate(values);
  });


  // Show the create form
  return (
    <Container size="md" py="md">
      <Group justify="space-between" mb="md">
        <Group>
          <ActionIcon variant="subtle" onClick={() => navigate('/databases')}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2}>Create Database</Title>
        </Group>
      </Group>

      <ServiceHealthWarning
        services={['db_cloner', 'kubernetes']}
        message="Database operations may be unavailable or delayed"
      />

      {snapshotsError && (
        <Alert icon={<IconInfoCircle />} title="Error" color="red" mb="md">
          Failed to load snapshots: {(snapshotsError as Error).message}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Card withBorder>
            <Stack gap="md">
              <Text fw={500} size="lg">
                Database Configuration
              </Text>
              <Divider />

              {/* Database Name */}
              <TextInput
                label="Database Name"
                placeholder="my-test-database"
                description="Name used as Kubernetes resource identifier (lowercase letters, numbers, hyphens only)"
                required
                {...form.getInputProps('name')}
              />

              {/* Snapshot Selector */}
              <Select
                label="Database Snapshot"
                placeholder="Select a snapshot..."
                description="Choose a snapshot to restore from"
                required
                data={
                  snapshotsData?.items?.map((snapshot: any) => ({
                    value: snapshot.name,
                    label: `${snapshot.name} (${snapshot.size}, ${parseUTCDate(snapshot.created_at)?.toLocaleDateString() || 'Unknown date'})`,
                  })) || []
                }
                disabled={snapshotsLoading}
                leftSection={snapshotsLoading ? <Loader size="xs" /> : undefined}
                {...form.getInputProps('snapshot')}
              />

              {/* TTL */}
              <Select
                label="Time to Live (TTL)"
                description="How long until the database is automatically deleted"
                required
                data={[
                  { value: '1', label: '1 hour' },
                  { value: '4', label: '4 hours' },
                  { value: '24', label: '24 hours (1 day)' },
                  { value: '168', label: '7 days (1 week)' },
                  { value: '720', label: '30 days (1 month)' },
                ]}
                value={form.values.ttl_hours.toString()}
                onChange={(value) => form.setFieldValue('ttl_hours', parseInt(value || '168', 10))}
              />

              <Alert icon={<IconInfoCircle />} title="Database Credentials" color="blue">
                <Text size="sm">
                  After creation, the database will be accessible using the shared credentials stored in the cluster.
                  Connection details will be available on the database detail page once ready.
                </Text>
              </Alert>
            </Stack>
          </Card>

          <Group justify="space-between">
            <Button variant="light" onClick={() => navigate('/databases')}>
              Cancel
            </Button>
            <Button
              type="submit"
              leftSection={<IconPlus size={16} />}
              loading={createMutation.isPending}
            >
              Create Database
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}

